import { useAppStore } from "@hooks/useAppStore";
import type { BaseItemSchema } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import HighlightIncorrectWords from "@memorang/ui/components/StudySession/SessionContent/item-types/HighlightIncorrectWords";
import { useCallback, useEffect, useMemo } from "react";
import FormativeTimeOverrunContainer from "../../../containers/FormativeTimeOverrunContainer";
import {
	getAllowAudioReplay,
	getSummativeResponseConfig,
} from "../../../helpers/response-timing";
import useAIFeedbackStore from "../../../hooks/useAIFeedbackStore";
import useChoiceInteraction from "../../../hooks/useChoiceInteraction";
import useResponseControlsStore from "../../../hooks/useResponseControlsStore";
import DesktopFeedbackPanel from "./DesktopFeedbackPanel";

type Props = {
	item: BaseItemSchema;
	isSplitLayout: boolean;
	isSummativeTest?: boolean;
};

const HighlightWordsContainer = ({
	item,
	isSplitLayout,
	isSummativeTest,
}: Props) => {
	const { content, response } = item;
	const itemId = item.id!;

	const { isAnswered, inReviewMode, selectedChoiceIds, handleSelectChoice } =
		useChoiceInteraction(item);

	const setUserResponse = useAIFeedbackStore((s) => s.setUserResponse);

	useEffect(() => {
		if (isAnswered) {
			return;
		}
		const ids = selectedChoiceIds ?? [];
		setUserResponse(itemId, ids.length > 0 ? [...ids] : undefined);
	}, [itemId, selectedChoiceIds, isAnswered, setUserResponse]);

	const correctAnswerIds = useMemo(() => {
		if (response.type !== "INLINE_TEXT_SELECTION") {
			return undefined;
		}
		return (response.incorrectWords ?? []).map((w) => `word-${w.wordIndex}`);
	}, [response]);

	const transcriptText = content.stem?.text ?? "";
	const audioUri =
		content.stimulus?.type === "AUDIO"
			? (content.stimulus.media?.[0]?.url ?? "")
			: "";
	const instruction =
		response.type === "INLINE_TEXT_SELECTION"
			? (response.instruction ?? "")
			: "";
	const showTimerConfig = useAppStore((s) => Boolean(s.sessionView?.timer));
	const responseConfig = getSummativeResponseConfig(
		response,
		isSummativeTest || showTimerConfig,
	);
	const prepTime = responseConfig?.prepTime;
	const allowAudioReplay = getAllowAudioReplay(responseConfig, isSummativeTest);
	const markAudioComplete = useResponseControlsStore(
		(s) => s.markAudioComplete,
	);
	const handleAudioEnded = useCallback(
		() => markAudioComplete(itemId),
		[markAudioComplete, itemId],
	);

	return (
		<Box gap={16}>
			<HighlightIncorrectWords
				instruction={instruction}
				audioUri={audioUri}
				transcriptText={transcriptText}
				selectedChoiceIds={selectedChoiceIds ?? []}
				correctAnswerIds={isAnswered ? correctAnswerIds : undefined}
				isAnswered={isAnswered}
				inReviewMode={inReviewMode}
				disabled={isAnswered}
				onToggleWord={handleSelectChoice}
				prepTime={prepTime}
				allowAudioReplay={allowAudioReplay}
				onAudioEnded={handleAudioEnded}
			/>
			<FormativeTimeOverrunContainer />
			<DesktopFeedbackPanel isSplitLayout={isSplitLayout} />
		</Box>
	);
};

export default HighlightWordsContainer;
